import { isEqual } from '@openeducation/oe-js-utils';
import type { SlotProps } from '@openeducation/oe-rn-services';
import type { DefaultTFuncReturn } from 'i18next';
import * as React from 'react';
import type { StyleProp, ViewStyle } from 'react-native';

import PaginatedPanel from '../../molecules/paginated-panel/paginated-panel';
import { ToggleButtonGrid } from '../../molecules/toggle-button-grid/toggle-button-grid';

export interface ToggleButtonPaginatedGridProps<T> {
  prevButtonLabel?: DefaultTFuncReturn;
  nextButtonLabel?: DefaultTFuncReturn;
  pages: {
    label: string;
    items: SlotProps<T>[];
  }[];
  selectedItem?: T;
  centered?: boolean;
  style?: StyleProp<ViewStyle>;
  onItemSelected: (selectedValue?: T) => void;
}

export const ToggleButtonPaginatedGrid = <T extends unknown>({
  prevButtonLabel,
  nextButtonLabel,
  pages,
  centered,
  style,
  selectedItem,
  onItemSelected,
}: ToggleButtonPaginatedGridProps<T>) => {
  const [currentPage, setCurrentPage] = React.useState<number>(0);

  React.useEffect(() => {
    const itemFoundInPage = pages.findIndex((page, i) => {
      const itemFoundInItems = page.items.find((item) =>
        isEqual(selectedItem, item.value)
      );
      return itemFoundInItems ? i : 0;
    });
    setCurrentPage(itemFoundInPage > -1 ? itemFoundInPage : 0);
  }, [pages, selectedItem]);

  const handleItemSelected = React.useCallback(
    (selected?: T) => onItemSelected(selected),
    [onItemSelected]
  );

  return (
    <PaginatedPanel
      style={style}
      prevButtonLabel={prevButtonLabel}
      nextButtonLabel={nextButtonLabel}
      currentPage={currentPage}
      totalPages={pages.length - 1}
      onChange={setCurrentPage}
      title={pages[currentPage]?.label}
    >
      <ToggleButtonGrid<T>
        columns={3}
        selectedItem={selectedItem}
        items={pages[currentPage]?.items}
        onItemSelected={handleItemSelected}
        horizontalSpacing={4}
        horizontalAlignment={centered ? 'center' : 'space-between'}
      />
    </PaginatedPanel>
  );
};

export default ToggleButtonPaginatedGrid;
